// src/pages/CursoDetalhe.tsx
// Rota: /cursos/:id

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, BookOpen, Clock, Users, Star,
  Building2, GraduationCap, MapPin, Calendar, Loader2,
  AlertTriangle, CheckCircle, Hash,
} from 'lucide-react';
import { API_BASE } from '../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface CursoDetalhe {
  id: string;
  titulo: string;
  descricao: string;
  conteudo: string | null;
  preco: number;
  categoria: string;
  nivel: string;
  modalidade: string;
  instrutor: string;
  afiliado: string;
  afiliado_cidade: string | null;
  afiliado_estado: string | null;
  alunos: number;
  duracao: string;
  avaliacao: number;
  cargaHoraria: string | null;
  createdAt: string | null;
  status: string;
  coverImageUrl: string | null;
  externalUrl: string | null;
}


const mapApiCurso = (c: any): CursoDetalhe => ({
  id:              c.id,
  titulo:          c.courseName ?? c.nome ?? '',
  descricao:       c.description ?? c.descricao ?? '',
  conteudo:        c.content ?? c.conteudo ?? null,
  preco:           Number(c.price ?? c.preco ?? 0),
  categoria:       c.category ?? c.categoria ?? '',
  nivel:           c.level ?? c.nivel ?? 'basico',
  modalidade:      c.modality ?? c.modalidade ?? 'presencial',
  instrutor:       c.instructorName ?? c.instrutor ?? '',
  afiliado:        c.affiliate?.schoolName ?? c.affiliateName ?? '',
  afiliado_cidade: c.affiliate?.city ?? null,
  afiliado_estado: c.affiliate?.state ?? null,
  alunos:          c.studentsCount ?? c.alunos ?? 0,
  duracao:         c.durationHours ? `${c.durationHours}h` : '—',
  avaliacao:       Number(c.rating ?? c.avaliacao ?? 0),
  cargaHoraria:    c.workload ?? c.cargaHoraria ?? c.durationHours ?? '—',
  createdAt:       c.createdAt ?? null,
  status:          c.status ?? 'active',
  coverImageUrl:   c.coverImageUrl ?? c.imageUrl ?? null,
  externalUrl:     c.externalUrl ?? null,
});

const NIVEL_LABEL: Record<string, string> = {
  basico: 'Básico', intermediario: 'Intermediário', avancado: 'Avançado',
};
const NIVEL_COR: Record<string, string> = {
  basico: 'bg-emerald-100 text-emerald-800',
  intermediario: 'bg-amber-100 text-amber-800',
  avancado: 'bg-red-100 text-red-800',
};
const MODALIDADE_LABEL: Record<string, string> = {
  presencial: 'Presencial', online: 'Online', hibrido: 'Híbrido',
};

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

// ─── Componente ───────────────────────────────────────────────────────────────
const CursoDetalhePage: React.FC = () => {
  const { id }      = useParams<{ id: string }>();
  const navigate    = useNavigate();
  const [curso, setCurso]     = useState<CursoDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        // Tenta endpoint de detalhe direto
        const res = await fetch(`${API_BASE}/courses/${id}`);
        if (!res.ok) throw new Error('Curso não encontrado');
        const data = await res.json();
        setCurso(mapApiCurso(data.course ?? data));
      } catch (e: any) {
        setError(e.message ?? 'Erro ao carregar curso');
      } finally { setLoading(false); }
    };
    load();
  }, [id]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-10 h-10 text-amber-600 animate-spin"/>
        <p className="text-gray-500 text-sm">Carregando curso…</p>
      </div>
    </div>
  );

  // ── Erro ───────────────────────────────────────────────────────────────────
  if (error || !curso) return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="text-center max-w-md">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4"/>
        <h2 className="text-2xl font-black text-gray-900 mb-2">Curso não encontrado</h2>
        <p className="text-gray-500 mb-6">{error ?? 'Este curso não existe ou foi removido.'}</p>
        <Link to="/cursos"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-900 text-white font-bold hover:shadow-lg transition-all">
          <ArrowLeft className="w-4 h-4"/> Ver todos os cursos
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950"/>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -left-24 w-[500px] h-[500px] bg-amber-500 rounded-full blur-3xl"/>
          <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] bg-orange-500 rounded-full blur-3xl"/>
        </div>

        <button
  onClick={() => navigate('/cursos')}
  type="button"
  className="absolute top-5 left-5 md:top-7 md:left-8 z-20 flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white text-sm font-bold hover:bg-white/20 transition-all backdrop-blur-sm"
>
  <ArrowLeft className="w-4 h-4"/> Voltar
</button>

        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20 text-center">
          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-amber-200 text-xs font-bold uppercase tracking-wider">
              {curso.categoria}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${NIVEL_COR[curso.nivel] ?? 'bg-gray-100 text-gray-700'}`}>
              {NIVEL_LABEL[curso.nivel] ?? curso.nivel}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-bold">
              {MODALIDADE_LABEL[curso.modalidade] ?? curso.modalidade}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight max-w-3xl mx-auto mb-6">
            {curso.titulo}
          </h1>

          {curso.descricao && (
            <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto">
              {curso.descricao}
            </p>
          )}
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"/>
      </section>

      {/* ── CONTEÚDO ──────────────────────────────────────────────────── */}
      <div className="container mx-auto px-5 md:px-8 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-10">
            <Link to="/" className="hover:text-amber-600 transition-colors">Início</Link>
            <span>/</span>
            <Link to="/cursos" className="hover:text-amber-600 transition-colors">Cursos</Link>
            <span>/</span>
            <span className="text-gray-600 truncate max-w-[200px]">{curso.titulo}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ── COLUNA PRINCIPAL ────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-8">

              {/* Sobre o curso */}
              <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_20px_55px_-40px_rgba(0,0,0,.35)] p-6 md:p-8">
                <h2 className="text-lg font-black text-gray-900 mb-5 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-600"/> Sobre o curso
                </h2>

                {curso.descricao && (
                  <p className="text-amber-800 leading-relaxed font-medium border-l-4 border-amber-400 pl-4 mb-6">
                    {curso.descricao}
                  </p>
                )}

                {/* Conteúdo programático */}
                {curso.conteudo ? (
                  <div
                    className="text-gray-800 leading-[1.9] text-[15.5px] whitespace-pre-wrap"
                    style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                  >
                    {curso.conteudo}
                  </div>
                ) : (
                  <div className="py-8 text-center border border-dashed border-gray-200 rounded-2xl text-gray-400 text-sm">
                    Conteúdo programático em breve.
                  </div>
                )}
              </div>

              {/* Instrutor */}
              {curso.instrutor && (
                <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_20px_55px_-40px_rgba(0,0,0,.35)] p-6 md:p-8">
                  <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-amber-600"/> Instrutor
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-black text-white">
                        {curso.instrutor.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-black text-gray-900">{curso.instrutor}</p>
                      {curso.afiliado && (
                        <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-amber-500"/>
                          {curso.afiliado}
                          {curso.afiliado_cidade && ` — ${curso.afiliado_cidade}${curso.afiliado_estado ? `/${curso.afiliado_estado}` : ''}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── SIDEBAR ─────────────────────────────────────────────── */}
            <div className="space-y-5">

              {/* Card de preço + CTA */}
              <div className="bg-white rounded-3xl border border-amber-200 shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] overflow-hidden sticky top-24">
                {/* Capa do curso */}
                {curso.coverImageUrl ? (
                  <img src={curso.coverImageUrl} alt={curso.titulo}
                    className="w-full h-44 object-cover"/>
                ) : (
                  <div className="w-full h-44 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                    <BookOpen className="w-14 h-14 text-amber-400/60"/>
                  </div>
                )}
                <div className="p-6">
                <p className="text-3xl font-black text-amber-700 mb-1">
                  {curso.preco > 0 ? `R$ ${curso.preco.toFixed(2)}` : 'Gratuito'}
                </p>
                {curso.preco > 0 && (
                  <p className="text-xs text-gray-400 mb-5">à vista ou parcelado</p>
                )}

                {curso.externalUrl ? (
                  <a
                    href={curso.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-bold text-gray-900 hover:shadow-lg transition-all mb-3 text-sm"
                    style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}
                  >
                    Quero me matricular <ArrowRight className="w-4 h-4"/>
                  </a>
                ) : (
                  <Link to="/filiacao"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-bold text-gray-900 hover:shadow-lg transition-all mb-3 text-sm"
                    style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}
                  >
                    Quero me matricular <ArrowRight className="w-4 h-4"/>
                  </Link>
                )}
                <Link to="/fale-conosco"
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-bold text-gray-700 border border-gray-200 hover:bg-gray-50 transition-all text-sm">
                  Falar com a Federação
                </Link>
                </div>{/* /p-6 */}
              </div>

              {/* Detalhes */}
              <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_20px_55px_-40px_rgba(0,0,0,.35)] p-6">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-4">Detalhes</h3>
                <div className="space-y-3">
                  {[
                    { icon: Clock,    label: 'Duração',     value: curso.duracao !== '—' ? curso.duracao : null },
                    { icon: Users,    label: 'Alunos',      value: curso.alunos > 0 ? String(curso.alunos) : null },
                    { icon: Star,     label: 'Avaliação',   value: curso.avaliacao > 0 ? `${curso.avaliacao.toFixed(1)} / 5` : null },
                    { icon: MapPin,   label: 'Modalidade',  value: MODALIDADE_LABEL[curso.modalidade] ?? curso.modalidade },
                    { icon: Calendar, label: 'Adicionado',  value: fmt(curso.createdAt) },
                  ].filter(d => d.value).map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-amber-600"/>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="font-semibold text-gray-800">{value}</p>
                      </div>
                    </div>
                  ))}

                  {curso.afiliado && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-amber-600"/>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Escola</p>
                        <p className="font-semibold text-gray-800">{curso.afiliado}</p>
                        {curso.afiliado_cidade && (
                          <p className="text-xs text-gray-400">{curso.afiliado_cidade}{curso.afiliado_estado ? `/${curso.afiliado_estado}` : ''}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* O que inclui */}
              <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_20px_55px_-40px_rgba(0,0,0,.35)] p-6">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-4">Incluso</h3>
                <div className="space-y-2.5">
                  {[
                    'Certificado de conclusão',
                    'Material didático',
                    'Suporte do instrutor',
                    'Reconhecido pela FEPCSSP',
                  ].map(item => (
                    <div key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0"/>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Botão voltar rodapé */}
          <div className="mt-12 pt-8 border-t border-gray-100">
            <button onClick={() => navigate('/cursos')}
              className="flex items-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 font-bold text-gray-700 hover:bg-gray-50 transition-all text-sm">
              <ArrowLeft className="w-4 h-4"/> Ver todos os cursos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CursoDetalhePage;